/* eslint-disable max-lines */
import { ReducerChain, withScope } from "@gamedev-web/gamedev-sdk-core";

import {
  CrmAaaGameConceptStatusData,
  CrmAaaStatusData,
  CrmGameConceptStatusData,
  CrmIdXboxStatusData,
  CrmPitchStatusData,
} from "../../models/crmStatus";
import {
  AaaGameConceptData,
  FormsData,
  FormsDataTypes,
  IdXboxGameConceptData,
  IdXboxIntroFormData,
  PitchConceptData,
} from "../../models/forms";
import { Programs } from "../../models/programsConfig";
import { AaaGameInformationFormId } from "../../pages/AaaGameInformationPage/AaaGameInformationPageConstants";
import { GameInformationFormId } from "../../pages/GameInformationPage/GameInformationPageConstants";
import { IdXboxIntroFormId } from "../../pages/IdXboxIntroPage/IdXboxIntroPageConstants";
import { PitchInformationFormId } from "../../pages/PitchInformationPage/PitchInformationPageConstants";
import {
  AaaGameConceptDataSet,
  IdXboxFormsResponse,
  IdXboxGameConceptDataSet,
  PitchConceptDataSet,
  ProgramsDataResponse,
} from "../../server/crm/api/models/forms";
import { AaaGameConceptsActions, aaaGameConceptsActions } from "../actions/aaaActions";
import { GameConceptActions, gameConceptActions } from "../actions/gameConceptActions";
import { PitchConceptActions, pitchConceptActions } from "../actions/pitchConceptActions";
import { ProgramsActions, programsActions } from "../actions/programsActions";
import { ProgramsScope } from "../scopes";

const initialState: ProgramsState = {};

export type FormsState = ReadonlyDictionary<FormsData>;

export type ProgramsState = {
  [program: string]: ProgramsDataResponse;
};

export const addProgramData = (
  state: ProgramsState,
  action: ReturnType<typeof programsActions.addProgramData>
) => ({
  ...state,
  [action.program]: action.programData,
});

export const updateProgramEnrollmentStatus = (
  state: ProgramsState,
  action: ReturnType<typeof programsActions.updateProgramEnrollmentStatus>
) => {
  const { program, enrollmentStatus } = action;

  if (!program || !enrollmentStatus) {
    return state;
  }

  if (program === Programs.IdXbox) {
    const tempValue = state[program] as IdXboxFormsResponse;
    const newValue = {
      ...tempValue,
      studio: { ...tempValue.studio, partnerCenterEnrollmentStatus: enrollmentStatus },
    };

    return {
      ...state,
      [program]: newValue,
    };
  }

  return state;
};

export const updateSubForm = (
  state: ProgramsState,
  action: ReturnType<typeof programsActions.updateSubFormData>
): ProgramsState => {
  const subFormData = action.subFormData;
  const formId = subFormData?.formId;
  const subFormId = subFormData?.subFormId;

  const program = action.program;

  if (!formId || !subFormId) {
    return state;
  }

  const mainForm = state[program]?.forms[formId];
  if (!mainForm) {
    let subForms: FormsDataTypes;
    // The following is to ensure the formId is a valid one, plus ensuring TypeScript does not scream at form types
    switch (formId) {
      case IdXboxIntroFormId:
        subForms = {} as IdXboxIntroFormData;
        break;
      case GameInformationFormId:
        subForms = {} as IdXboxGameConceptData;
        break;
      case AaaGameInformationFormId:
        subForms = {} as AaaGameConceptData;
        break;
      case PitchInformationFormId:
        subForms = {} as PitchConceptData;
        break;
      default:
        return state;
    }

    return {
      ...state,
      [program]: {
        ...state[program],
        forms: {
          ...state[program]?.forms,
          [formId]: {
            formId: formId,
            subForms: {
              ...subForms,
              [subFormId]: subFormData.subForm,
            },
          },
        },
      },
    };
  }

  return {
    ...state,
    [program]: {
      ...state[program],
      forms: {
        ...state[program]?.forms,
        [formId]: {
          ...mainForm,
          subForms: {
            ...mainForm.subForms,
            [subFormId]: subFormData.subForm,
          },
        },
      },
    },
  };
};

export const updateSingleIdXboxGameConcept = (
  state: ProgramsState,
  action: ReturnType<typeof gameConceptActions.updateSingleIdXboxGameConcept>
) => {
  const program = Programs.IdXbox;
  const currentProgramData = state[program];
  const gameConcept = action.gameConcept;
  const currentGameConceptsList =
    (currentProgramData?.crmStatus as CrmGameConceptStatusData)?.gameConceptsList ?? {};
  const currentCrmStatus = currentProgramData?.crmStatus as CrmIdXboxStatusData;

  const newGameConceptList = {
    ...currentGameConceptsList,
    [gameConcept.state.id]: gameConcept,
  } as IdXboxGameConceptDataSet;
  const newCrmStatus: CrmIdXboxStatusData = {
    ...currentCrmStatus,
    gameConceptsList: newGameConceptList,
    currentGameConcept: {
      id: gameConcept.state.id,
      crmGameConceptStatus: gameConcept.state.status,
      crmGameConceptSubStatus: gameConcept.state.subStatus,
      crmGameConceptApprovalComments: gameConcept.state.approvalStatusComments,
      submittedDate: gameConcept.state.submittedDate,
      acceptedDate: gameConcept.state.acceptedDate,
      forms: gameConcept,
    },
  };

  return {
    ...state,
    [program]: {
      ...currentProgramData,
      forms: {
        ...currentProgramData?.forms,
        [GameInformationFormId]: {
          formId: GameInformationFormId,
          subForms: gameConcept,
        },
      },
      crmStatus: newCrmStatus,
    },
  };
};

const clearSelectedForm = (state: ProgramsState, program: Programs, formId: string) => {
  const currentProgramData = state[program];
  return {
    ...state,
    [program]: {
      ...currentProgramData,
      forms: {
        ...currentProgramData?.forms,
        [formId]: undefined,
      },
    },
  };
};

export const clearSelectedIdXboxGameConcept = (
  state: ProgramsState,
  _action: ReturnType<typeof gameConceptActions.clearSelectedIdXboxGameConcept>
) => {
  const program = Programs.IdXbox;
  return clearSelectedForm(state, program, GameInformationFormId);
};

export const clearSelectedAaaGameConcept = (
  state: ProgramsState,
  _action: ReturnType<typeof gameConceptActions.clearSelectedAaaGameConcept>
) => {
  const program = Programs.Aaa;
  return clearSelectedForm(state, program, AaaGameInformationFormId);
};

export const updateSingleAaaGameConcept = (
  state: ProgramsState,
  action: ReturnType<typeof gameConceptActions.updateSingleAaaGameConcept>
) => {
  const program = Programs.Aaa;
  const currentProgramData = state[program];
  const gameConcept = action.gameConcept;
  const currentGameConceptsList =
    (currentProgramData?.crmStatus as CrmGameConceptStatusData)?.gameConceptsList ?? {};
  const currentCrmStatus = currentProgramData?.crmStatus as CrmAaaStatusData;

  const newGameConceptList = {
    ...currentGameConceptsList,
    [gameConcept.state.id]: gameConcept,
  } as AaaGameConceptDataSet;
  const newCrmStatus: CrmAaaStatusData = {
    ...currentCrmStatus,
    gameConceptsList: newGameConceptList,
    currentGameConcept: {
      id: gameConcept.state.id,
      crmGameConceptStatus: gameConcept.state.status,
      crmGameConceptSubStatus: gameConcept.state.subStatus,
      crmGameConceptApprovalComments: gameConcept.state.approvalStatusComments,
      submittedDate: gameConcept.state.submittedDate,
      acceptedDate: gameConcept.state.acceptedDate,
    },
  };

  return {
    ...state,
    [program]: {
      ...currentProgramData,
      forms: {
        ...currentProgramData?.forms,
        [AaaGameInformationFormId]: {
          formId: AaaGameInformationFormId,
          subForms: gameConcept,
        },
      },
      crmStatus: newCrmStatus,
    },
  };
};

export const updateSinglePitchConcept = (
  state: ProgramsState,
  action: ReturnType<typeof pitchConceptActions.updateSinglePitchConcept>
) => {
  const program = Programs.Pitch;
  const pitchConcept = action.pitchConcept;
  const currentProgramData = state[program];
  const currentPitchConceptsList =
    (currentProgramData?.crmStatus as CrmPitchStatusData)?.pitchConceptLists ?? {};
  const currentCrmPitchStatus = currentProgramData?.crmStatus as CrmPitchStatusData;

  const newPitchConceptList: PitchConceptDataSet = {
    ...currentPitchConceptsList,
    [pitchConcept.state.id]: pitchConcept,
  };
  const newCrmPitchStatus: CrmPitchStatusData = {
    ...currentCrmPitchStatus,
    pitchConceptLists: newPitchConceptList,
    currentPitchConcept: {
      id: pitchConcept.state.id,
      crmPitchConceptStatus: pitchConcept.state.status,
      crmPitchConceptApprovalComments: pitchConcept.state.approvalStatusComments,
    },
  };
  return {
    ...state,
    [program]: {
      ...currentProgramData,
      forms: {
        ...currentProgramData?.forms,
        [PitchInformationFormId]: {
          formId: PitchInformationFormId,
          subForms: pitchConcept,
        },
      },
      crmStatus: newCrmPitchStatus,
    },
  };
};

export function updateGameConceptsList(
  state: ProgramsState,
  action: ReturnType<typeof aaaGameConceptsActions.updateConceptsList>
) {
  const currentCrmStatus = state[action.program]?.crmStatus as CrmAaaGameConceptStatusData;

  const newGameConceptList = action.conceptsList as AaaGameConceptDataSet;
  const newCrmStatus: CrmAaaGameConceptStatusData = {
    ...currentCrmStatus,
    gameConceptsList: newGameConceptList,
    gameConceptsListQuery: action.conceptsListQuery,
  };
  const finalState = {
    ...state,
    [action.program]: {
      ...state[action.program],
      crmStatus: newCrmStatus,
    },
  };
  return finalState;
}

export function clearGameConceptQuery(
  state: ProgramsState,
  action: ReturnType<typeof aaaGameConceptsActions.clearGameConceptListQuery>
) {
  const currentCrmStatus = state[action.program]?.crmStatus as CrmAaaGameConceptStatusData;

  const newCrmStatus: CrmAaaGameConceptStatusData = {
    ...currentCrmStatus,
    gameConceptsListQuery: undefined,
  };
  const finalState = {
    ...state,
    [action.program]: {
      ...state[action.program],
      crmStatus: newCrmStatus,
    },
  };
  return finalState;
}

export const programsReducer = withScope(
  [ProgramsScope],
  new ReducerChain<ProgramsState>(initialState)
    .on(ProgramsActions.ADD_PROGRAM_DATA, addProgramData)
    .on(ProgramsActions.UPDATE_SUB_FORM, updateSubForm)
    .on(ProgramsActions.UPDATE_PROGRAM_ENROLLMENT_STATUS, updateProgramEnrollmentStatus)
    .on(GameConceptActions.UPDATE_SINGLE_IDXBOX_GAME_CONCEPT, updateSingleIdXboxGameConcept)
    .on(GameConceptActions.CLEAR_SELECTED_IDXBOX_GAME_CONCEPT, clearSelectedIdXboxGameConcept)
    .on(GameConceptActions.CLEAR_SELECTED_AAA_GAME_CONCEPT, clearSelectedAaaGameConcept)
    .on(GameConceptActions.UPDATE_SINGLE_AAA_GAME_CONCEPT, updateSingleAaaGameConcept)
    .on(PitchConceptActions.UPDATE_SINGLE_PITCH_CONCEPT, updateSinglePitchConcept)
    .on(AaaGameConceptsActions.GET_GAME_CONCEPT_LIST, updateGameConceptsList)
    .on(AaaGameConceptsActions.CLEAR_GAME_CONCEPT_QUERY, clearGameConceptQuery)
    .build()
);
